/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { saveTier3, isValidPIVA } from '@/lib/onboarding';
import { validatePartitaIva } from '@/lib/antiFraud';
import { ErrorAlert } from './parts';

// -----------------------------------------------------------------------------
// TIER 3: Clinic & Billing
// -----------------------------------------------------------------------------
const PAYMENT_OPTIONS = [
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'insurance', label: 'Insurance' },
];

export const Tier3Step = ({ user, onDone }) => {
  const [form, setForm] = useState({
    clinic_address: user?.clinic_address || '',
    piva: user?.piva || '',
    visit_price_eur: user?.visit_price_eur || 25,
    payment_methods: user?.payment_methods_accepted || ['card'],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const togglePayment = (value) => {
    setForm((f) => {
      const has = f.payment_methods.includes(value);
      return { ...f, payment_methods: has ? f.payment_methods.filter((v) => v !== value) : [...f.payment_methods, value] };
    });
  };

  const canSubmit =
    form.clinic_address.trim().length > 0 &&
    Number(form.visit_price_eur) > 0 &&
    form.payment_methods.length > 0 &&
    (!form.piva || isValidPIVA(form.piva));

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await saveTier3({
        clinic_address: form.clinic_address,
        piva: form.piva || null,
        visit_price_eur: Number(form.visit_price_eur),
        payment_methods: form.payment_methods,
      });
      toast.success('Onboarding complete — your profile is now bookable!');
      onDone && onDone();
    } catch (e) { setError(e); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="tier3-step">
      <div className="mb-4">
        <h2 className="font-display font-bold text-xl mb-1">Tier 3 — Clinic & billing</h2>
        <p className="text-sm text-muted-foreground">
          Provide your consultation setup. This information activates online booking and enables payments
          through the platform. Partita IVA is used only for invoicing.
        </p>
      </div>
      <ErrorAlert error={error} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label htmlFor="addr">Clinic address</Label>
          <Input id="addr" data-testid="tier3-address"
            placeholder="Via Roma 12, 20121 Milano MI"
            value={form.clinic_address}
            onChange={(e) => setForm({ ...form, clinic_address: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="piva">Partita IVA (optional)</Label>
          <Input id="piva" data-testid="tier3-piva"
            maxLength={11} placeholder="12345678901"
            value={form.piva}
            onChange={(e) => setForm({ ...form, piva: e.target.value.replace(/\D/g, '') })} />
          {form.piva && !isValidPIVA(form.piva) && (
            <p className="text-xs text-destructive mt-1">Partita IVA must be exactly 11 digits.</p>
          )}
          {form.piva && isValidPIVA(form.piva) && !validatePartitaIva(form.piva).valid && (
            <p className="text-xs text-warning mt-1" data-testid="tier3-piva-checksum-warning">Attenzione: {validatePartitaIva(form.piva).reason}.</p>
          )}
          {form.piva && validatePartitaIva(form.piva).valid && (
            <p className="text-xs text-success mt-1" data-testid="tier3-piva-checksum-ok">Partita IVA formalmente valida (checksum OK).</p>
          )}
        </div>
        <div>
          <Label htmlFor="fee">Consultation fee (€)</Label>
          <Input id="fee" data-testid="tier3-fee" type="number" min="1" step="0.5"
            value={form.visit_price_eur}
            onChange={(e) => setForm({ ...form, visit_price_eur: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <Label>Accepted payment methods</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {PAYMENT_OPTIONS.map((p) => {
              const active = form.payment_methods.includes(p.value);
              return (
                <button key={p.value} type="button" onClick={() => togglePayment(p.value)}
                  data-testid={`tier3-payment-${p.value}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-secondary'
                  }`}>{p.label}</button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={handleSave} disabled={!canSubmit || saving} data-testid="tier3-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Complete onboarding
        </Button>
      </div>
    </div>
  );
};
